import * as React from 'react';
import React__default, { memo, useMemo, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { Heading, Text } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';
import { useIntl } from '@grandvisionhq/state';
import { IconLink, Util, Loader, Tabs, Tab, MediaQuery, Button, GoogleMaps, toast, Autocomplete } from '@grandvisionhq/common';
import { DateUtils, useDebouncedEffect } from '@grandvisionhq/utils';
import { gql, useLazyQuery } from '@grandvisionhq/graphql';

var LocationVariant;
(function(LocationVariant) {
    LocationVariant["ListItem"] = "ListItem";
    LocationVariant["StandAlone"] = "StandAlone";
    LocationVariant["MapItem"] = "MapItem";
})(LocationVariant || (LocationVariant = {}));

const ADDRESS_CONFIGURATION_SIZES = [
    'MINIMAL',
    'SMALL',
    'MEDIUM',
    'LARGE',
    'COMPLETE', 
];
const OPENING_HOURS_DAYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday', 
];
var OpenClosedStatus;
(function(OpenClosedStatus) {
    OpenClosedStatus["Open"] = 'OPEN';
    OpenClosedStatus["Closing"] = 'CLOSING';
    OpenClosedStatus["Closed"] = 'CLOSED';
})(OpenClosedStatus || (OpenClosedStatus = {}));

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ADDRESS_CONFIGURATION_SIZES: ADDRESS_CONFIGURATION_SIZES,
  OPENING_HOURS_DAYS: OPENING_HOURS_DAYS,
  get OpenClosedStatus () { return OpenClosedStatus; },
  get LocationVariant () { return LocationVariant; }
});

const defaultValue = {
    MINIMAL: [],
    SMALL: [],
    MEDIUM: [],
    LARGE: [],
    COMPLETE: []
};
const AddressConfigurationContext = /*#__PURE__*/ React__default.createContext(defaultValue);
const AddressConfigurationProvider = ({ children , settings =defaultValue  })=>/*#__PURE__*/ React__default.createElement(AddressConfigurationContext.Provider, {
        value: settings
    }, children)
;
const useAddressConfiguration = ()=>React__default.useContext(AddressConfigurationContext)
;

function _extends$k() {
    _extends$k = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$k.apply(this, arguments);
}
function _objectWithoutProperties$i(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$i(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$i(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Address = (_param)=>{
    var { className , rows , children  } = _param, rest = _objectWithoutProperties$i(_param, [
        "className",
        "rows",
        "children"
    ]);
    return rows.length || Boolean(children) || Array.isArray(children) && children.length > 0 ? /*#__PURE__*/ React__default.createElement("address", _extends$k({}, rest, {
        className: classnames('address-stores-v2', className)
    }), rows.map((row, index)=>/*#__PURE__*/ React__default.createElement("span", {
            key: `${row}-${index}`,
            className: "address-stores-v2__row"
        }, row)
    ), children) : null;
};

const getAddressRows = (size, config)=>config[size]
;
const parseAddress = (addressRows, addressData)=>{
    const replacements1 = Object.entries(addressData).map(([key, value])=>({
            regExp: new RegExp(`\{${key}\}`, 'g'),
            value
        })
    );
    const inject = (row, replacements)=>replacements.reduce((acc, { regExp , value  })=>value === undefined ? acc : acc.replace(regExp, String(value))
        , row)
    ;
    const clean = (row)=>{
        const unusedTemplateRegExp = new RegExp(/{([^}]+)}/, 'g');
        return row.trim().replace(unusedTemplateRegExp, '').split(',').map((el)=>el.trim()
        ).filter((el)=>!!el
        ).join(', ');
    };
    return addressRows.map((row)=>inject(row, replacements1)
    ).map(clean);
};

function _extends$j() {
    _extends$j = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$j.apply(this, arguments);
}
function _objectWithoutProperties$h(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$h(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$h(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const AddressContainer = (_param)=>{
    var { size ='COMPLETE' , data ={}  } = _param, rest = _objectWithoutProperties$h(_param, [
        "size",
        "data"
    ]);
    const config = useAddressConfiguration();
    const rows = React__default.useMemo(()=>{
        const templateRows = getAddressRows(size, config);
        if (templateRows.length) {
            return parseAddress(templateRows, data);
        }
        const feedback = `No configuration for address size ${size}`;
        console.error(feedback);
        return [
            `[${feedback}]`
        ];
    }, [
        size,
        config,
        data
    ]);
    return(/*#__PURE__*/ React__default.createElement(Address, _extends$j({}, rest, {
        rows: rows
    })));
};

function _extends$i() {
    _extends$i = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$i.apply(this, arguments);
}
function _objectWithoutProperties$g(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$g(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$g(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DefaultLocationVariant = LocationVariant.ListItem;
const Location = (_param)=>{
    var { className , id , variant =DefaultLocationVariant , name , addressSize , addressData , emailAddress , phone , phoneHidden , emailHidden , highlight =false , distanceDetails =null , formattedDistance , note =null , buttons =null , openingHours , locationDetailsUrl , wheelchairAccessible , onCloseClick , onNameClick , formattedAddress , primaryButton , secondaryButton  } = _param, rest = _objectWithoutProperties$g(_param, [
        "className",
        "id",
        "variant",
        "name",
        "addressSize",
        "addressData",
        "emailAddress",
        "phone",
        "phoneHidden",
        "emailHidden",
        "highlight",
        "distanceDetails",
        "formattedDistance",
        "note",
        "buttons",
        "openingHours",
        "locationDetailsUrl",
        "wheelchairAccessible",
        "onCloseClick",
        "onNameClick",
        "formattedAddress",
        "primaryButton",
        "secondaryButton"
    ]);
    const { getLabel  } = useIntl();
    const showFormattedAddress = formattedAddress && (!addressSize || !addressData);
    return(/*#__PURE__*/ React__default.createElement("article", _extends$i({}, rest, {
        id: id !== undefined ? `${id}` : id,
        className: classnames('location-item', {
            'location-item--highlight': highlight,
            'location-item--list-item': variant === LocationVariant.ListItem,
            'location-item--stand-alone': variant === LocationVariant.StandAlone,
            'location-item--map-item': variant === LocationVariant.MapItem
        }, className)
    }), /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__part"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__content"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        className: "location-item__heading",
        component: variant === LocationVariant.ListItem || variant === LocationVariant.MapItem ? 'h2' : 'h1',
        variant: variant === LocationVariant.ListItem ? 'smallest' : 'small'
    }, renderHeadingContent()), variant === LocationVariant.MapItem && onCloseClick && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__close-button",
        "data-t": "close-button"
    }, /*#__PURE__*/ React__default.createElement("button", {
        onClick: onCloseClick
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "close"
    }))), /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__address",
        "data-t": "location-item-address"
    }, showFormattedAddress ? /*#__PURE__*/ React__default.createElement("address", null, formattedAddress) : /*#__PURE__*/ React__default.createElement(AddressContainer, {
        size: addressSize,
        data: addressData
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__contact"
    }, !phoneHidden && phone && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__contact-field"
    }, /*#__PURE__*/ React__default.createElement("a", {
        href: `tel:${phone}`,
        "data-t": "Phone"
    }, phone)), !emailHidden && emailAddress && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__contact-field"
    }, /*#__PURE__*/ React__default.createElement("a", {
        href: `mailto:${emailAddress}`,
        "data-t": "Email"
    }, emailAddress))))), distanceDetails && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__distance-details"
    }, distanceDetails), wheelchairAccessible && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__wheelchair-accessible"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "wheelchair",
        className: "location-item__wheelchair-accessible__icon"
    }), getLabel('stores.page.details.wheelchairAccessible')), formattedDistance && /*#__PURE__*/ React__default.createElement("span", {
        className: "location-item__distance",
        "data-t": "location-item-distance"
    }, formattedDistance)), note, variant === LocationVariant.StandAlone && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__actions"
    }, secondaryButton, primaryButton, buttons)), /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__part"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__opening-hours"
    }, openingHours), (variant === LocationVariant.ListItem || variant === LocationVariant.MapItem) && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-item__actions"
    }, secondaryButton, primaryButton, buttons))));
    function renderHeadingContent() {
        if (locationDetailsUrl) {
            return(/*#__PURE__*/ React__default.createElement("a", {
                "data-t": `location-link-${id}`,
                className: "location-item__link",
                href: locationDetailsUrl
            }, name));
        }
        if (onNameClick) {
            return(/*#__PURE__*/ React__default.createElement("button", {
                "data-t": `location-link-${id}`,
                className: "location-item__link",
                onClick: ()=>onNameClick(id)
            }, name));
        }
        return name;
    }
};

const scheme = {
    phone: 'tel',
    email: 'mailto'
};
const icon = {
    phone: 'phone',
    email: 'mail'
};
const LocationContactField = ({ className ='' , fieldType , value  })=>/*#__PURE__*/ React.createElement(IconLink, {
        className: className,
        href: `${scheme[fieldType]}:${value}`,
        iconName: icon[fieldType]
    }, value)
;

function _extends$h() {
    _extends$h = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$h.apply(this, arguments);
}
function _objectWithoutProperties$f(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$f(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$f(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function LocationDistance(_param) {
    var { className , distance , labels , timeByCar , timeByFoot  } = _param, rest = _objectWithoutProperties$f(_param, [
        "className",
        "distance",
        "labels",
        "timeByCar",
        "timeByFoot"
    ]);
    const isEmpty = !(distance || timeByCar || timeByFoot);
    if (isEmpty) return null;
    return(/*#__PURE__*/ React__default.createElement("div", _extends$h({
        className: classnames('location-distance', className)
    }, rest), /*#__PURE__*/ React__default.createElement("div", {
        className: "location-distance__content"
    }, distance && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-distance__item"
    }, /*#__PURE__*/ React__default.createElement(Text, {
        className: "location-distance__text",
        component: "span",
        variant: "body-head"
    }, labels.distance)), timeByFoot && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-distance__item"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "walking"
    }), /*#__PURE__*/ React__default.createElement("span", {
        className: "location-distance__visually-hidden"
    }, labels.walking), /*#__PURE__*/ React__default.createElement(Text, {
        className: "location-distance__text",
        component: "span"
    }, timeByFoot)), timeByCar && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-distance__item"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "driving"
    }), /*#__PURE__*/ React__default.createElement("span", {
        className: "location-distance__visually-hidden"
    }, labels.driving), /*#__PURE__*/ React__default.createElement(Text, {
        className: "location-distance__text location-distance__text--space",
        component: "span"
    }, timeByCar)))));
}
var LocationDistance$1 = /*#__PURE__*/ memo(LocationDistance);

function _defineProperty$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _extends$g() {
    _extends$g = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$g.apply(this, arguments);
}
function _objectSpread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$2(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties$e(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$e(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$e(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function LocationDistanceContainer(_param) {
    var { distance , labels ={}  } = _param, rest = _objectWithoutProperties$e(_param, [
        "distance",
        "labels"
    ]);
    const { getLabel , getFormattedLabel  } = useIntl();
    const defaultLabels = useMemo(()=>_objectSpread$2({
            distance: getFormattedLabel('stores.location.distance', {
                labelData: {
                    distance: distance || ''
                }
            }),
            driving: getLabel('stores.location.drivingTime'),
            walking: getLabel('stores.location.walkingTime')
        }, labels)
    , [
        distance,
        labels,
        getFormattedLabel,
        getLabel
    ]);
    return(/*#__PURE__*/ React__default.createElement(LocationDistance$1, _extends$g({
        distance: distance,
        labels: defaultLabels
    }, rest)));
}
var LocationDistanceContainer$1 = /*#__PURE__*/ memo(LocationDistanceContainer);

function _extends$f() {
    _extends$f = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$f.apply(this, arguments);
}
function _objectWithoutProperties$d(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$d(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$d(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SkipToMapLink = (_param)=> {
    var { onClick , text , href  } = _param, rest = _objectWithoutProperties$d(_param, [
        "onClick",
        "text",
        "href"
    ]);
    return React.createElement("div", _extends$f({}, rest, {
        className: "locations-skip-to-map-link"
    }), /*#__PURE__*/ React.createElement("a", {
        onClick: onClick,
        href: href,
        className: "locations-skip-to-map-link__link"
    }, text));
};

function _extends$e() {
    _extends$e = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$e.apply(this, arguments);
}
function _objectWithoutProperties$c(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$c(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$c(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SkipToMapLinkContainer = (_param)=>{
    var { mapId  } = _param, rest = _objectWithoutProperties$c(_param, [
        "mapId"
    ]);
    const { getLabel  } = useIntl();
    // We cannot create a ref for the tabindex div that needs to be focused because it is generated
    // from the GoogleMaps library. Therefore, focus is managed here with vanilla js instead.
    const onClick = (event)=>{
        var ref, ref1;
        event.preventDefault();
        (ref1 = (ref = document.getElementById(mapId)) === null || ref === void 0 ? void 0 : ref.querySelector('[tabindex="0"]')) === null || ref1 === void 0 ? void 0 : ref1.focus();
    };
    return(/*#__PURE__*/ React.createElement(SkipToMapLink, _extends$e({}, rest, {
        href: `#${mapId}`,
        onClick: onClick,
        text: getLabel('stores.page.search.skipToMapLink')
    })));
};

const LocationList = ({ children , scrollToTargetId , scrollContainer  })=>{
    const locationRefs = useRef({});
    useEffect(()=>{
        if (typeof scrollToTargetId === 'number') {
            const targetElem = locationRefs.current[scrollToTargetId];
            if (targetElem) {
                const offset = targetElem.offsetTop;
                // If we have a scrollContainer element provided, scroll inside that,
                // if not, we scroll the window to the target offset
                if (scrollContainer) {
                    Util.scrollToItem(scrollContainer, offset, 500);
                } else {
                    window.scrollTo(0, offset);
                }
            }
        }
    }, [
        scrollToTargetId,
        scrollContainer
    ]);
    return(/*#__PURE__*/ React__default.createElement("ul", {
        className: "location-list"
    }, React__default.Children.map(children, (child)=>/*#__PURE__*/ React__default.createElement("li", {
            key: child.key,
            className: "location-list__item",
            ref: (el)=>{
                var _key;
                locationRefs.current[(_key = child.key) !== null && _key !== void 0 ? _key : ''] = el;
            }
        }, child)
    )));
};

function _extends$d() {
    _extends$d = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$d.apply(this, arguments);
}
function _objectWithoutProperties$b(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$b(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$b(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const LocationSearchList = (_param)=> {
    var { children , className , loading  } = _param, rest = _objectWithoutProperties$b(_param, [
        "children",
        "className",
        "loading"
    ]);
    return React__default.createElement("div", _extends$d({}, rest, {
        className: classnames('location-search-list', className)
    }), loading ? /*#__PURE__*/ React__default.createElement("div", {
        className: "location-search-list__loader"
    }, /*#__PURE__*/ React__default.createElement(Loader, null)) : /*#__PURE__*/ React__default.createElement(LocationList, null, children));
};

function _extends$c() {
    _extends$c = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$c.apply(this, arguments);
}
function _objectWithoutProperties$a(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$a(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$a(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const LocationSearchListLayout = (_param)=> {
    var { className , loadingListItems , listItems , topContent , searchForm , showTabs , infoContent , showLinkToMap , googleMapsId  } = _param, rest = _objectWithoutProperties$a(_param, [
        "className",
        "loadingListItems",
        "listItems",
        "topContent",
        "searchForm",
        "showTabs",
        "infoContent",
        "showLinkToMap",
        "googleMapsId"
    ]);
    return React__default.createElement("div", _extends$c({}, rest, {
        className: classnames('location-search-list-layout', className)
    }), topContent, /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('location-search-list-layout__search-form', {
            'location-search-list-layout__search-form--hidden': showTabs
        })
    }, searchForm, showLinkToMap && googleMapsId && /*#__PURE__*/ React__default.createElement(SkipToMapLinkContainer, {
        mapId: googleMapsId
    })), /*#__PURE__*/ React__default.createElement(LocationSearchList, {
        loading: loadingListItems
    }, listItems), infoContent);
};

function _extends$b() {
    _extends$b = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$b.apply(this, arguments);
}
function _objectWithoutProperties$9(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$9(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$9(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const LocationSearchTabs = (_param)=>{
    var { className , labels , tabIndex , onChange , listContent , mapContent , showTabs =false , tabsRef  } = _param, rest = _objectWithoutProperties$9(_param, [
        "className",
        "labels",
        "tabIndex",
        "onChange",
        "listContent",
        "mapContent",
        "showTabs",
        "tabsRef"
    ]);
     return React__default.createElement("div", _extends$b({}, rest, {
        ref: tabsRef,
        className: classnames('location-search-tabs', className)
    }), /*#__PURE__*/ React__default.createElement(Tabs, {
        activeTabIndex: tabIndex,
        onChange: onChange
    }, /*#__PURE__*/ React__default.createElement(Tab, {
        className: classnames('location-search-tabs__tab', {
            'location-search-tabs__tab--hidden': !showTabs
        }),
        label: /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Icon, {
            className: "location-search-tabs__tab-icon",
            name: "list"
        }), /*#__PURE__*/ React__default.createElement("span", {
            className: "location-search-tabs__tab-label"
        }, labels.listTab))
    }), /*#__PURE__*/ React__default.createElement(Tab, {
        onClick: ()=>{
            var ref;
            return tabsRef === null || tabsRef === void 0 ? void 0 : (ref = tabsRef.current) === null || ref === void 0 ? void 0 : ref.scrollIntoView();
        },
        className: classnames('location-search-tabs__tab', {
            'location-search-tabs__tab--hidden': !showTabs
        }),
        label: /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Icon, {
            className: "location-search-tabs__tab-icon",
            name: "location"
        }), /*#__PURE__*/ React__default.createElement("span", {
            className: "location-search-tabs__tab-label"
        }, labels.mapTab))
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "location-search-tabs__content"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('location-search-tabs__content-item', 'location-search-tabs__content-item-list', {
            'location-search-tabs__content-item--active': tabIndex === 0
        })
    }, listContent), /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('location-search-tabs__content-item', 'location-search-tabs__content-item-map', {
            'location-search-tabs__content-item--active': tabIndex === 1
        })
    }, mapContent)));
};

function _extends$a() {
    _extends$a = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$a.apply(this, arguments);
}
const LocationSearchTabsContainer = (props)=>{
    const { getLabel  } = useIntl();
    const [tabIndex, setTabIndex] = React__default.useState(0);
    const ref = /*#__PURE__*/ React__default.createRef();
    const labels = React__default.useMemo(()=>({
            listTab: getLabel('locations.search.tabs.list'),
            mapTab: getLabel('locations.search.tabs.map')
        })
    , [
        getLabel
    ]);
    var _showTabs;
    return(/*#__PURE__*/ React__default.createElement(LocationSearchTabs, _extends$a({}, props, {
        tabsRef: ref,
        labels: labels,
        showTabs: (_showTabs = props.showTabs) !== null && _showTabs !== void 0 ? _showTabs : false,
        tabIndex: tabIndex,
        onChange: setTabIndex
    })));
};

function _extends$9() {
    _extends$9 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$9.apply(this, arguments);
}
function _objectWithoutProperties$8(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$8(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$8(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const LocationSearch = (_param)=> {
    var { className , searchForm , listContent , listTopContent , mapContent , showTabs =false , scrollToHandlerId  } = _param, rest = _objectWithoutProperties$8(_param, [
        "className",
        "searchForm",
        "listContent",
        "listTopContent",
        "mapContent",
        "showTabs",
        "scrollToHandlerId"
    ]);
    return React__default.createElement("article", _extends$9({}, rest, {
        className: classnames('location-search', className)
    }), listTopContent, showTabs && /*#__PURE__*/ React__default.createElement("div", {
        id: scrollToHandlerId,
        className: "location-search__search-form"
    }, searchForm), /*#__PURE__*/ React__default.createElement("div", {
        className: "location-search__wrapper"
    }, /*#__PURE__*/ React__default.createElement(LocationSearchTabsContainer, {
        showTabs: showTabs,
        listContent: listContent,
        mapContent: mapContent
    })));
};

function _extends$8() {
    _extends$8 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$8.apply(this, arguments);
}
function _objectWithoutProperties$7(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$7(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$7(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const LocationSearchContainer = (_param)=>{
    var { searchForm , loadingListItems , listItems , listTopContent , listInfoContent , mapContent , isPristineSearch , googleMapsId  } = _param, rest = _objectWithoutProperties$7(_param, [
        "searchForm",
        "loadingListItems",
        "listItems",
        "listTopContent",
        "listInfoContent",
        "mapContent",
        "isPristineSearch",
        "googleMapsId"
    ]);
    const isMobile = MediaQuery.useIsPhone();
    const showTabs = isMobile && !isPristineSearch;
    return(/*#__PURE__*/ React__default.createElement(LocationSearch, _extends$8({}, rest, {
        showTabs: showTabs,
        searchForm: searchForm,
        listTopContent: isMobile && listTopContent,
        scrollToHandlerId: "location-search-form",
        listContent: /*#__PURE__*/ React__default.createElement(LocationSearchListLayout, {
            googleMapsId: googleMapsId,
            showLinkToMap: !isMobile,
            topContent: !isMobile && listTopContent ? listTopContent : null,
            searchForm: searchForm,
            showTabs: showTabs,
            infoContent: listInfoContent !== null && listInfoContent !== void 0 ? listInfoContent : null,
            loadingListItems: loadingListItems,
            listItems: listItems
        }),
        mapContent: mapContent
    })));
};

function _extends$7() {
    _extends$7 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$7.apply(this, arguments);
}
function _objectWithoutProperties$6(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$6(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$6(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const LocationNote = (_param)=>{
    var { className , note  } = _param, rest = _objectWithoutProperties$6(_param, [
        "className",
        "note"
    ]);
    return note ? /*#__PURE__*/ React__default.createElement("small", _extends$7({}, rest, {
        className: classnames('location-note', className)
    }), note) : null;
};

const getDayName$1 = (index)=>OPENING_HOURS_DAYS[index]
;
const getHoursAndMinutes = (time)=>time.split(time.indexOf('.') > -1 ? '.' : ':').map((t)=>parseInt(t)
    )
;
const getSpecialOpeningHoursForDate = (specialOpeningHours, date)=>{
    return specialOpeningHours === null || specialOpeningHours === void 0 ? void 0 : specialOpeningHours.find(({ dateOpenClosed  })=>new Date(dateOpenClosed).getDate() === date.getDate() && new Date(dateOpenClosed).getMonth() === date.getMonth()
    );
};
const getRegularOpeningHoursForDate = (openingHours, date)=>openingHours[getDayName$1(date.getDay())]
;
const getDateFromTime = (time)=>{
    const [hours, minutes] = getHoursAndMinutes(time);
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    return date;
};
const isOpen = (hours)=>{
    const now = new Date();
    return hours === null || hours === void 0 ? void 0 : hours.some(({ open , close  })=>{
        const openDate = getDateFromTime(open);
        const closeDate = getDateFromTime(close);
        return now.getTime() >= openDate.getTime() && now.getTime() < closeDate.getTime();
    });
};
const isClosingSoon = (hours)=>{
    const now = new Date();
    return hours === null || hours === void 0 ? void 0 : hours.some(({ close  })=>{
        const closeDate = getDateFromTime(close);
        const diff = Math.floor((closeDate.getTime() - now.getTime()) / 1000 / 60);
        return diff > 0 && diff < 60;
    });
};
const isClosed = (hours)=>{
    const now = new Date();
    return hours === null || hours === void 0 ? void 0 : hours.every(({ close  })=>{
        const closeDate = getDateFromTime(close);
        return now.getTime() > closeDate.getTime();
    });
};
const getOpenCloseStatus = (openingHours, specialOpeningHours)=>{
    const now = new Date();
    const openingHoursToday = getRegularOpeningHoursForDate(openingHours, now);
    const specialOpeningHoursToday = getSpecialOpeningHoursForDate(specialOpeningHours, now);
    let open, closingSoon, closed;
    if (specialOpeningHoursToday) {
        open = specialOpeningHoursToday.openClosed === 'OPEN' && isOpen(specialOpeningHoursToday.openClosedTime);
        closingSoon = isClosingSoon(specialOpeningHoursToday.openClosedTime);
        closed = !open || isClosed(specialOpeningHoursToday.openClosedTime);
    } else {
        open = isOpen(openingHoursToday);
        closingSoon = isClosingSoon(openingHoursToday);
        closed = isClosed(openingHoursToday);
    }
    if (closed) {
        return OpenClosedStatus.Closed;
    }
    if (closingSoon) {
        return OpenClosedStatus.Closing;
    }
    return open ? OpenClosedStatus.Open : OpenClosedStatus.Closed;
};
const getOpenCloseIndication = (openingHours, specialOpeningHours, timeFormatter)=>{
    const today = new Date();
    const tomorrow = DateUtils.shiftDate(today, 'day', 1);
    const status = getOpenCloseStatus(openingHours, specialOpeningHours);
    const specialOpeningHoursToday = getSpecialOpeningHoursForDate(specialOpeningHours, today);
    const specialOpeningHoursTomorrow = getSpecialOpeningHoursForDate(specialOpeningHours, tomorrow);
    const regularOpeningHoursToday = getRegularOpeningHoursForDate(openingHours, today);
    const regularOpeningHoursTomorrow = getRegularOpeningHoursForDate(openingHours, tomorrow);
    if (status === OpenClosedStatus.Open || status === OpenClosedStatus.Closing) {
        var ref;
        return {
            hours: (ref = (specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : specialOpeningHoursToday.openClosedTime) || regularOpeningHoursToday) === null || ref === void 0 ? void 0 : ref.map(({ open , close  })=>[
                    timeFormatter(getDateFromTime(open)),
                    timeFormatter(getDateFromTime(close)), 
                ]
            )
        };
    }
    if (status === OpenClosedStatus.Closed) {
        let nextHours;
        if ((specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : specialOpeningHoursToday.openClosed) === OpenClosedStatus.Open || (regularOpeningHoursToday === null || regularOpeningHoursToday === void 0 ? void 0 : regularOpeningHoursToday.length)) {
            var ref1;
            if (specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : (ref1 = specialOpeningHoursToday.openClosedTime) === null || ref1 === void 0 ? void 0 : ref1.length) {
                var ref2;
                nextHours = specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : (ref2 = specialOpeningHoursToday.openClosedTime) === null || ref2 === void 0 ? void 0 : ref2.find(({ open  })=>getDateFromTime(open).getTime() > today.getTime()
                );
            } else if (regularOpeningHoursToday.length) {
                nextHours = regularOpeningHoursToday.find(({ open  })=>getDateFromTime(open).getTime() > today.getTime()
                );
            }
            if (nextHours) {
                return {
                    opensAt: 'TODAY',
                    hours: [
                        [
                            timeFormatter(getDateFromTime(nextHours.open))
                        ]
                    ]
                };
            }
        }
        if ((specialOpeningHoursTomorrow === null || specialOpeningHoursTomorrow === void 0 ? void 0 : specialOpeningHoursTomorrow.openClosed) === OpenClosedStatus.Open || (regularOpeningHoursTomorrow === null || regularOpeningHoursTomorrow === void 0 ? void 0 : regularOpeningHoursTomorrow.length)) {
            var ref3;
            if (specialOpeningHoursTomorrow === null || specialOpeningHoursTomorrow === void 0 ? void 0 : (ref3 = specialOpeningHoursTomorrow.openClosedTime) === null || ref3 === void 0 ? void 0 : ref3.length) {
                nextHours = specialOpeningHoursTomorrow.openClosedTime[0];
            } else if ((specialOpeningHoursTomorrow === null || specialOpeningHoursTomorrow === void 0 ? void 0 : specialOpeningHoursTomorrow.openClosed) !== OpenClosedStatus.Closed && regularOpeningHoursTomorrow.length) {
                nextHours = regularOpeningHoursTomorrow[0];
            }
            if (nextHours) {
                return {
                    opensAt: 'TOMORROW',
                    hours: [
                        [
                            timeFormatter(getDateFromTime(nextHours.open))
                        ]
                    ]
                };
            }
        }
    }
    return {
        hours: []
    };
};

const getAddressFromSuggestions = (suggestions, id)=>{
    const suggestion1 = suggestions.find((suggestion)=>suggestion.id === id
    );
    const { title ='' , subTitle  } = suggestion1 !== null && suggestion1 !== void 0 ? suggestion1 : {};
    return `${title}${subTitle ? ', ' + subTitle : ''}`;
};

function _extends$6() {
    _extends$6 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$6.apply(this, arguments);
}
function _objectWithoutProperties$5(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$5(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$5(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const OpenCloseIndication = (_param)=>{
    var { labels , status , hours , isExpanded , onExpand , onKeyDown  } = _param, rest = _objectWithoutProperties$5(_param, [
        "labels",
        "status",
        "hours",
        "isExpanded",
        "onExpand",
        "onKeyDown"
    ]);
     return React__default.createElement("button", _extends$6({}, rest, {
        "data-t": "OpenCloseIndication",
        className: classnames('open-close-indication', {
            'open-close-indication--expandable': Boolean(onExpand)
        }),
        tabIndex: Boolean(onExpand) ? 0 : -1,
        onClick: onExpand,
        onKeyDown: onKeyDown
    }), /*#__PURE__*/ React__default.createElement("p", {
        className: classnames('open-close-indication__indication', {
            [`open-close-indication__indication--${status.toLowerCase()}`]: true
        })
    }, /*#__PURE__*/ React__default.createElement("span", {
        className: "open-close-indication__status"
    }, labels.status), labels.indication, ' ', hours === null || hours === void 0 ? void 0 : hours.map((timePair)=>/*#__PURE__*/ React__default.createElement("span", {
            key: timePair.toString(),
            className: "open-close-indication__time-pair"
        }, timePair.length > 1 ? /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("time", {
            dateTime: timePair[0]
        }, timePair[0]), ` - `, /*#__PURE__*/ React__default.createElement("time", {
            dateTime: timePair[1]
        }, timePair[1])) : /*#__PURE__*/ React__default.createElement("time", {
            key: timePair[0],
            dateTime: timePair[0]
        }, timePair[0]))
    )), onExpand && /*#__PURE__*/ React__default.createElement(Icon, {
        className: "open-close-indication__icon",
        name: isExpanded ? 'chevron-up' : 'chevron-down'
    }));
};

function _extends$5() {
    _extends$5 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$5.apply(this, arguments);
}
function _objectWithoutProperties$4(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$4(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$4(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const OpenCloseIndicationContainer = (_param)=>{
    var { openingHours , specialOpeningHours , onExpand  } = _param, rest = _objectWithoutProperties$4(_param, [
        "openingHours",
        "specialOpeningHours",
        "onExpand"
    ]);
    const [mounted, setMounted] = React.useState(false);
    const [time, setTime] = React.useState(Date.now());
    const { dateTime , getLabel  } = useIntl();
    const status = React.useMemo(()=>getOpenCloseStatus(openingHours, specialOpeningHours)
    , [
        // when the time state is set, it should trigger re-memoization
        time,
        openingHours,
        specialOpeningHours, 
    ]);
    const indication = React.useMemo(()=>getOpenCloseIndication(openingHours, specialOpeningHours, dateTime.formatTime)
    , // when the time state is set, it should trigger re-memoization
    [
        time,
        openingHours,
        specialOpeningHours,
        dateTime.formatTime
    ]);
    const labels = React.useMemo(()=>({
            status: getLabel(`locations.openCloseIndication.status.${status}`),
            indication: indication.opensAt ? getLabel(`locations.openCloseIndication.indication.${indication.opensAt}`) : ''
        })
    , // when the time state is set, it should trigger re-memoization
    [
        time,
        getLabel,
        indication.opensAt,
        status
    ]);
    React.useEffect(()=>{
        const interval = setInterval(()=>{
            setTime(Date.now());
        }, 60000);
        return ()=>{
            clearInterval(interval);
        };
    }, []);
    React.useEffect(()=>{
        setMounted(true);
    }, []);
    const onKeyDown = React.useCallback((event)=>{
        if (!onExpand) {
            return;
        }
        switch(event.key){
            case 'ArrowLeft':
                onExpand(false);
                break;
            case 'ArrowRight':
                onExpand(true);
                break;
        }
    }, [
        onExpand
    ]);
    return mounted ? /*#__PURE__*/ React.createElement(OpenCloseIndication, _extends$5({}, rest, {
        labels: labels,
        status: status,
        hours: indication.hours,
        onExpand: onExpand,
        onKeyDown: onKeyDown
    })) : null;
};

function _extends$4() {
    _extends$4 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$4.apply(this, arguments);
}
function _objectWithoutProperties$3(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$3(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$3(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const OpeningHours = (_param)=>{
    var { className , labels , regular , openingHours , specialOpeningHours , special =[] , isExpanded , onExpand , withTitle  } = _param, rest = _objectWithoutProperties$3(_param, [
        "className",
        "labels",
        "regular",
        "openingHours",
        "specialOpeningHours",
        "special",
        "isExpanded",
        "onExpand",
        "withTitle"
    ]);
     return React__default.createElement("div", _extends$4({}, rest, {
        className: classnames('location-opening-hours', {
            ['location-opening-hours--expanded']: isExpanded
        }, className)
    }), withTitle && /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "location-opening-hours__heading"
    }, labels.title), /*#__PURE__*/ React__default.createElement(OpenCloseIndicationContainer, {
        openingHours: openingHours,
        specialOpeningHours: specialOpeningHours,
        isExpanded: isExpanded,
        onExpand: onExpand
    }), /*#__PURE__*/ React__default.createElement("div", {
        className: "location-opening-hours__lists"
    }, (special === null || special === void 0 ? void 0 : special.length) ? /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "subheading2",
        component: "h3",
        className: "location-opening-hours__list-heading"
    }, labels.specialTitle), /*#__PURE__*/ React__default.createElement("dl", {
        className: "location-opening-hours__list"
    }, special.map(({ label , status , entries  })=>/*#__PURE__*/ React__default.createElement(React__default.Fragment, {
            key: label
        }, /*#__PURE__*/ React__default.createElement("dt", {
            className: classnames('location-opening-hours__day')
        }, label), /*#__PURE__*/ React__default.createElement("dd", {
            className: classnames('location-opening-hours__hours', {
                'location-opening-hours__hours--closed': status === 'CLOSED'
            })
        }, entries.map(({ key , value  })=>/*#__PURE__*/ React__default.createElement("span", {
                key: key,
                className: "location-opening-hours__hours-entry"
            }, value)
        )))
    )), /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "subheading2",
        component: "span",
        className: "location-opening-hours__list-heading"
    }, labels.regularTitle)) : null, /*#__PURE__*/ React__default.createElement("dl", {
        className: "location-opening-hours__list"
    }, regular.map(({ label , today , status , entries  })=>/*#__PURE__*/ React__default.createElement(React__default.Fragment, {
            key: label
        }, /*#__PURE__*/ React__default.createElement("dt", {
            className: classnames('location-opening-hours__day', {
                'location-opening-hours__day--today': today
            })
        }, label), /*#__PURE__*/ React__default.createElement("dd", {
            className: classnames('location-opening-hours__hours', {
                'location-opening-hours__hours--today': today,
                'location-opening-hours__hours--closed': status === 'CLOSED'
            })
        }, entries.map(({ key , value  })=>/*#__PURE__*/ React__default.createElement("span", {
                key: key,
                className: "location-opening-hours__hours-entry"
            }, value)
        )))
    ))));
};

function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties$2(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$2(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$2(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var deprecatedPropsToProps = (props)=>{
    // Omit deprecated props from "rest"
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { schedule , highlightCurrentDay  } = props, rest = _objectWithoutProperties$2(props, [
        "schedule",
        "highlightCurrentDay"
    ]);
    const openingHours = Object.entries(schedule).reduce((acc, [key, value])=>{
        const day = Object.keys(acc).find((dayName)=>key.includes(dayName.slice(0, 3)) && key.includes('OpenClose')
        );
        if (day && value) {
            acc[day] = value.map(({ open , close  })=>{
                const openChars = [
                    ...String(open)
                ];
                const closeChars = [
                    ...String(close)
                ];
                if (!openChars.includes(':') && !openChars.includes('.')) {
                    openChars.splice(-2, 0, ':');
                }
                if (!closeChars.includes(':') && !closeChars.includes('.')) {
                    closeChars.splice(-2, 0, ':');
                }
                return {
                    open: openChars.join(''),
                    close: closeChars.join('')
                };
            });
        }
        return acc;
    }, {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
    });
    return _objectSpread$1({
        openingHours
    }, rest);
};

function _extends$3() {
    _extends$3 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$3.apply(this, arguments);
}
function _objectWithoutProperties$1(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var ref;
const isToday = (day)=>((ref = OPENING_HOURS_DAYS[new Date().getDay()]) !== null && ref !== void 0 ? ref : '') === day
;
const getDayName = (date)=>OPENING_HOURS_DAYS[date.getDay()]
;
const hasDeprecatedProps = (props)=>'schedule' in props
;
const OpeningHoursContainer = (props)=>{
    const _ref = hasDeprecatedProps(props) ? deprecatedPropsToProps(props) : props, { openingHours , specialOpeningHours =[] , isExpanded: initialIsExpanded = false , isExpandable =true , onExpandCallback  } = _ref, rest = _objectWithoutProperties$1(_ref, [
        "openingHours",
        "specialOpeningHours",
        "isExpanded",
        "isExpandable",
        "onExpandCallback"
    ]);
    const [isExpanded, setIsExpanded] = React__default.useState(initialIsExpanded);
    const onExpandHandler = React__default.useCallback((doExpand)=>{
        setIsExpanded((current)=>typeof doExpand === 'boolean' ? doExpand : !current
        );
        if (typeof onExpandCallback === 'function') {
            onExpandCallback();
        }
    }, [
        onExpandCallback
    ]);
    const { getLabel , dateTime: { formatMidsizeDate  } ,  } = useIntl();
    const labels = React__default.useMemo(()=>({
            title: getLabel('locations.openingHours.title'),
            specialTitle: getLabel('locations.openingHours.specialTitle'),
            regularTitle: getLabel('locations.openingHours.regularTitle')
        })
    , [
        getLabel
    ]);
    const regular = React__default.useMemo(()=>Object.entries(openingHours).map(([day, items])=>{
            const isOpen = items.length > 0;
            const entries = isOpen ? items.map(({ open , close  })=>({
                    key: `${day}-${open}-${close}`,
                    value: `${open} - ${close}`
                })
            ) : [
                {
                    key: `${day}-closed`,
                    value: getLabel('locations.openingHours.closed')
                }
            ];
            return {
                label: getLabel(`locations.openingHours.days.long.${day}`),
                today: isToday(day),
                status: isOpen ? 'OPEN' : 'CLOSED',
                entries
            };
        })
    , [
        openingHours,
        getLabel
    ]);
    const special = React__default.useMemo(()=>(specialOpeningHours !== null && specialOpeningHours !== void 0 ? specialOpeningHours : []).map(({ openClosed , dateOpenClosed , openClosedTime  })=>{
            const date = new Date(dateOpenClosed);
            const isOpen = openClosed === 'OPEN';
            const entries = isOpen ? (openClosedTime !== null && openClosedTime !== void 0 ? openClosedTime : []).map(({ open , close  })=>({
                    key: `${dateOpenClosed}-${open}-${close}`,
                    value: `${open} - ${close}`
                })
            ) : [
                {
                    key: `${dateOpenClosed}-closed`,
                    value: getLabel('locations.openingHours.closed')
                }, 
            ];
            return {
                label: `${getLabel(`locations.openingHours.days.short.${getDayName(date)}`)}, ${formatMidsizeDate(date)}`,
                status: openClosed,
                entries
            };
        })
    , [
        specialOpeningHours,
        formatMidsizeDate,
        getLabel
    ]);
    React__default.useEffect(()=>setIsExpanded(initialIsExpanded)
    , [
        initialIsExpanded
    ]);
    return(/*#__PURE__*/ React__default.createElement(OpeningHours, _extends$3({}, rest, {
        labels: labels,
        regular: regular,
        special: special,
        openingHours: openingHours,
        specialOpeningHours: specialOpeningHours,
        isExpanded: isExpanded,
        onExpand: isExpandable ? onExpandHandler : undefined
    })));
};

const UseMyLocationButton = ({ onClick , caption , isLoading  })=>/*#__PURE__*/ React__default.createElement(Button, {
        size: "small",
        variant: "ghost",
        icon: "gps",
        className: "use-my-location",
        onClick: onClick,
        caption: caption,
        isLoading: isLoading
    })
;

function _extends$2() {
    _extends$2 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$2.apply(this, arguments);
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const LocationSearchMap = (_param)=> {
    var { className , labels , markers , activeLocation , mapOptions , highlight , hovered , userLocation , userLocationZoomLevel , centerOnUserLocation , onClickUseMyLocation , onMarkerClick , onMarkerHover , onMapClick , center , markersUpdateConfig , id  } = _param, rest = _objectWithoutProperties(_param, [
        "className",
        "labels",
        "markers",
        "activeLocation",
        "mapOptions",
        "highlight",
        "hovered",
        "userLocation",
        "userLocationZoomLevel",
        "centerOnUserLocation",
        "onClickUseMyLocation",
        "onMarkerClick",
        "onMarkerHover",
        "onMapClick",
        "center",
        "markersUpdateConfig",
        "id"
    ]);
    return React__default.createElement("div", _extends$2({}, rest, {
        className: classnames('location-search-map', className)
    }), /*#__PURE__*/ React__default.createElement("style", {
        jsx: true,
        global: true
    }, `
        .gm-style .gm-style-iw-t:after {
          display: none;
        }
      `), /*#__PURE__*/ React__default.createElement(GoogleMaps, {
        id: id,
        center: center,
        labels: {
            shareCurrentLocation: labels.shareCurrentLocation
        },
        markers: markers,
        highlight: highlight,
        hovered: hovered,
        mapOptions: mapOptions,
        showUseMyLocation: true,
        userLocation: userLocation,
        userLocationZoomLevel: userLocationZoomLevel,
        centerOnUserLocation: centerOnUserLocation,
        markersUpdateConfig: markersUpdateConfig,
        onClickUseMyLocation: onClickUseMyLocation,
        onMarkerClick: onMarkerClick,
        onMarkerHover: onMarkerHover,
        onMapClick: onMapClick
    }), activeLocation && /*#__PURE__*/ React__default.createElement("div", {
        className: "location-search-map__active-location"
    }, activeLocation // we render the active location outside the GoogleMaps on mobile
    ));
};

function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
const LocationSearchMapContainer = (props)=>{
    const { getLabel  } = useIntl();
    const labels = React__default.useMemo(()=>({
            shareCurrentLocation: getLabel('locations.search.map.shareCurrentLocation')
        })
    , [
        getLabel
    ]);
    const mapOptions = {
        minZoom: 3,
        maxZoom: 19,
        styles: [
            {
                featureType: 'poi.business',
                stylers: [
                    {
                        visibility: 'off'
                    }
                ]
            }, 
        ]
    };
    return(/*#__PURE__*/ React__default.createElement(LocationSearchMap, _extends$1({}, props, {
        labels: labels,
        mapOptions: mapOptions
    })));
};

const defaultSettings = {
    enableHighAccuracy: false,
    timeout: Infinity,
    maximumAge: 0
};
const useLocation = (watch = false, options)=>{
    const settings = Object.assign({}, defaultSettings, options);
    const intl = useIntl();
    const [position, setPosition] = React.useState(undefined);
    const [error1, setError] = React.useState(null);
    const [isUseMyLocation, setIsUseMyLocation] = React.useState(false);
    const [isMyLocationRequested, setIsMyLocationRequested] = React.useState(false);
    const [isPositionLoading, setIsPositionLoading] = React.useState(false);
    const [hasPermissionDeniedError, setHasPermissionDeniedError] = React.useState(false);
    const onChange = ({ coords , timestamp  })=>{
        setPosition({
            lat: coords.latitude,
            lng: coords.longitude,
            accuracy: coords.accuracy,
            timestamp
        });
        setIsPositionLoading(false);
    };
    const onError = (error)=>{
        setIsPositionLoading(false);
        setIsUseMyLocation(false);
        console.warn('Failed to get user coords:', error.message);
        switch(error.code){
            case error.PERMISSION_DENIED:
                setError(intl.getLabel('stores.yourLocation.permissionDenied'));
                toast.warning('stores.yourLocation.permissionDenied');
                break;
            default:
                setError(intl.getLabel('stores.yourLocation.error'));
                toast.warning('stores.yourLocation.error');
        }
    };
    const checkPermissionDeniedStatus = (state)=>{
        setHasPermissionDeniedError(state === 'denied');
    };
    React.useEffect(()=>{
        // Check first if geolocation permissions denied in the browser to show notification on button click.
        if ('permissions' in navigator) {
            navigator.permissions.query({
                name: 'geolocation'
            }).then((permissionStatus)=>{
                checkPermissionDeniedStatus(permissionStatus.state);
                // Check permissions if user change setting in the browser
                permissionStatus.addEventListener('change', ()=>{
                    checkPermissionDeniedStatus(permissionStatus.state);
                });
            });
        } else {
            // Internet Explorer, Safari, WebView Android and Safari on iOS do not support this feature by now,
            // set setHasPermissionDeniedError to false in this case.
            // In such browsers we will execute watchPosition or getCurrentPosition
            // to check and set correct value to hasPermissionDeniedError.
            setHasPermissionDeniedError(false);
        }
    }, []);
    React.useEffect(()=>{
        if (!isMyLocationRequested) {
            setIsUseMyLocation(false);
        }
        if (isMyLocationRequested) {
            setError(null);
            if (hasPermissionDeniedError) {
                setError(intl.getLabel('stores.yourLocation.permissionDenied'));
                toast.warning('stores.yourLocation.permissionDenied');
                setIsMyLocationRequested(false);
                setIsPositionLoading(false);
                return;
            }
            const hasBrowserSupport = typeof window !== 'undefined' && typeof window.navigator !== 'undefined' && typeof window.navigator.geolocation !== 'undefined';
            if (!hasBrowserSupport) {
                setError(intl.getLabel('stores.yourLocation.unsupported'));
                toast.warning('stores.yourLocation.unsupported');
                setIsMyLocationRequested(false);
                return;
            }
            setIsPositionLoading(true);
            setIsUseMyLocation(true);
            const geo = window.navigator.geolocation;
            let watcher = null;
            if (watch) {
                watcher = geo.watchPosition(onChange, onError, settings);
            } else {
                geo.getCurrentPosition(onChange, onError, settings);
            }
            return ()=>{
                watcher && geo.clearWatch(watcher);
            };
        }
    }, [
        isMyLocationRequested,
        hasPermissionDeniedError
    ]);
    return React.useMemo(()=>({
            position,
            isUseMyLocation,
            isPositionLoading,
            isMyLocationRequested,
            error: error1,
            setIsMyLocationRequested
        })
    , [
        position,
        isUseMyLocation,
        isPositionLoading,
        isMyLocationRequested,
        error1,
        setIsMyLocationRequested, 
    ]);
};

const placePredictions = gql`
  fragment PlacePrediction on PlacePrediction {
    id
    locality
    sublocality
  }
`;
const GET_PLACE_PREDICTIONS = gql`
  query GetPlacePredictions($name: String!) {
    placePredictions(name: $name) {
      ...PlacePrediction
    }
  }
  ${placePredictions}
`;

const placeDetails = gql`
  fragment PlaceDetails on PlaceDetails {
    city
    country
    countryCode
    id
    postalCode
    province
    provinceShort
    streetName
    streetNumber
  }
`;
const GET_PLACE_DETAILS = gql`
  query GetPlaceDetailsById($id: String!) {
    placeDetailsById(id: $id) {
      ...PlaceDetails
    }
  }
  ${placeDetails}
`;

const fromQueryPlacePredictionsToPlacePredictions = (data)=>{
    if (!data) {
        return null;
    }
    return data.map(({ id , locality , sublocality  })=>({
            id,
            locality,
            sublocality: sublocality !== null && sublocality !== void 0 ? sublocality : undefined
        })
    );
};

const fromQueryPlaceDetailsToPlaceDetails = (data)=>{
    if (!data) {
        return null;
    }
    var _city, _country, _countryCode, _postalCode, _province, _provinceShort, _streetName, _streetNumber;
    return {
        id: data.id,
        city: (_city = data.city) !== null && _city !== void 0 ? _city : undefined,
        country: (_country = data.country) !== null && _country !== void 0 ? _country : undefined,
        countryCode: (_countryCode = data.countryCode) !== null && _countryCode !== void 0 ? _countryCode : undefined,
        postalCode: (_postalCode = data.postalCode) !== null && _postalCode !== void 0 ? _postalCode : undefined,
        province: (_province = data.province) !== null && _province !== void 0 ? _province : undefined,
        provinceShort: (_provinceShort = data.provinceShort) !== null && _provinceShort !== void 0 ? _provinceShort : undefined,
        streetName: (_streetName = data.streetName) !== null && _streetName !== void 0 ? _streetName : undefined,
        streetNumber: (_streetNumber = data.streetNumber) !== null && _streetNumber !== void 0 ? _streetNumber : undefined
    };
};

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
const usePlacePredictions = (options)=>{
    const [getPlacePredictions, { error , loading , data  }] = useLazyQuery(GET_PLACE_PREDICTIONS, _objectSpread({}, options));
    return React__default.useMemo(()=>{
        return [
            getPlacePredictions,
            {
                data: fromQueryPlacePredictionsToPlacePredictions(data === null || data === void 0 ? void 0 : data.placePredictions),
                loading,
                error
            }, 
        ];
    }, [
        data,
        loading,
        error,
        getPlacePredictions
    ]);
};

const usePlaceDetails = ()=>{
    const [getPlaceDetails, { error , loading , data  }] = useLazyQuery(GET_PLACE_DETAILS);
    return React__default.useMemo(()=>{
        return [
            getPlaceDetails,
            {
                data: fromQueryPlaceDetailsToPlaceDetails(data === null || data === void 0 ? void 0 : data.placeDetailsById),
                loading,
                error
            }, 
        ];
    }, [
        getPlaceDetails,
        data === null || data === void 0 ? void 0 : data.placeDetailsById,
        loading,
        error
    ]);
};

const mapPlacePredictionToSuggestion = (options)=>options.map(({ locality , sublocality , id  })=>({
            id,
            title: locality,
            subTitle: sublocality
        })
    )
;

const AddressSearchForm$1 = ({ labels , loading , isSuggestionSelected , minSearchCharacterCount , onChange , onClear , onSubmit , onSuggestionClick , searchQuery , suggestions ,  })=>/*#__PURE__*/ React.createElement("div", {
        className: "address-search"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "address-search__search-form"
    }, /*#__PURE__*/ React.createElement("form", {
        role: "search",
        action: "",
        onSubmit: (event)=>{
            event && event.preventDefault();
        }
    }, /*#__PURE__*/ React.createElement(Autocomplete, {
        isSuggestionSelected: isSuggestionSelected,
        labels: labels,
        loading: loading,
        minCountCharactersToSubmit: minSearchCharacterCount,
        name: "address-search",
        onChange: onChange,
        onClear: onClear,
        onSubmit: onSubmit,
        onSuggestionClick: onSuggestionClick,
        showClearButton: Boolean(searchQuery),
        submitIcon: /*#__PURE__*/ React.createElement(Icon, {
            name: "search"
        }),
        suggestions: suggestions,
        type: "search",
        value: searchQuery
    }))))
;

const MIN_SEARCH_CHARACTER_COUNT = 2;
const AddressSearchFormContainer = ({ onResult , minSearchCharacterCount =MIN_SEARCH_CHARACTER_COUNT  })=>{
    const [searchQuery, setSearchQuery] = React.useState('');
    const [suggestions, setSuggestions] = React.useState([]);
    const [suggestionSelected, setSuggestionSelected] = React.useState(false);
    const { getLabel  } = useIntl();
    const [getPlacePredictions, placePredictions] = usePlacePredictions({
        variables: {
            name: searchQuery
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache'
    });
    const [getPlaceDetails, placeDetails] = usePlaceDetails();
    const labels = React.useMemo(()=>({
            placeholder: getLabel('locations.addressAutoComplete.placeholder'),
            noResults: getLabel('locations.addressAutoComplete.noResults')
        })
    , [
        getLabel
    ]);
    const handleOnChange = (value)=>{
        setSearchQuery(value);
    };
    const onChange = React.useCallback((event)=>handleOnChange(event.currentTarget.value)
    , []);
    const handleOnSearch = React.useCallback(()=>{
        if (searchQuery.length >= minSearchCharacterCount && !suggestionSelected) {
            getPlacePredictions({
                variables: {
                    name: searchQuery
                }
            });
        }
    }, [
        searchQuery,
        minSearchCharacterCount,
        suggestionSelected,
        getPlacePredictions
    ]);
    useDebouncedEffect(()=>{
        handleOnSearch();
    }, 500, [
        searchQuery
    ]);
    const onSubmit = React.useCallback(()=>{
        suggestions.length > 0 && getPlaceDetails({
            variables: {
                id: suggestions[0].id
            }
        });
    }, [
        getPlaceDetails,
        suggestions
    ]);
    const onSuggestionClick = React.useCallback((id)=>{
        setSearchQuery(getAddressFromSuggestions(suggestions, id));
        setSuggestionSelected(true);
        setSuggestions([]);
        id && getPlaceDetails({
            variables: {
                id
            }
        });
    }, [
        getPlaceDetails,
        suggestions
    ]);
    const onClear = React.useCallback(()=>{
        setSearchQuery('');
        setSuggestions([]);
        setSuggestionSelected(false);
    }, []);
    React.useEffect(()=>{
        placeDetails.data && onResult(placeDetails.data);
    }, [
        placeDetails.data,
        onResult
    ]);
    React.useEffect(()=>{
        if (placePredictions.data) {
            setSuggestions(mapPlacePredictionToSuggestion(placePredictions.data));
        }
    }, [
        placePredictions
    ]);
    React.useEffect(()=>{
        if (placePredictions.error) {
            toast.failed('locations.getPlacePredictions.error');
            console.error('Failed to fetch place predictions: ', placePredictions.error);
        }
        if (placeDetails.error) {
            toast.failed('locations.getPlaceDetails.error');
            console.error('Failed to fetch place details: ', placeDetails.error);
        }
    }, [
        placePredictions.error,
        placeDetails.error
    ]);
    return(/*#__PURE__*/ React.createElement(AddressSearchForm$1, {
        isSuggestionSelected: suggestionSelected,
        labels: labels,
        loading: placePredictions.loading || placeDetails.loading,
        minSearchCharacterCount: minSearchCharacterCount,
        onChange: onChange,
        onClear: onClear,
        onSubmit: onSubmit,
        onSuggestionClick: onSuggestionClick,
        searchQuery: searchQuery,
        suggestions: suggestions
    }));
};

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
const AddressSearchForm = (props)=>/*#__PURE__*/ React.createElement(AddressSearchFormContainer, _extends({}, props))
;

export { AddressContainer as Address, AddressConfigurationProvider, AddressSearchForm, Location, LocationContactField, LocationDistanceContainer$1 as LocationDistance, LocationList, LocationNote, LocationSearchContainer as LocationSearch, LocationSearchList, LocationSearchListLayout, LocationSearchMapContainer as LocationSearchMap, LocationSearchTabsContainer as LocationSearchTabs, OpenCloseIndicationContainer as OpenCloseIndication, OpeningHoursContainer as OpeningHours, index as Types, UseMyLocationButton, getAddressFromSuggestions, getOpenCloseIndication, getOpenCloseStatus, mapPlacePredictionToSuggestion, useLocation, usePlaceDetails, usePlacePredictions };
//# sourceMappingURL=index.js.map
